import { useEffect, useState } from "react";
import { crDateTime } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Clock, Lock, MailWarning, PauseCircle } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return null;
  return crDateTime(iso) || iso;
};

export default function MailCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [pausing, setPausing] = useState(null); // {scope:'group'|'kind', id, label}
  const [pauseDate, setPauseDate] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/email-switches");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el Centro de correos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Si la dueña cambia el interruptor de Pagos en otra pestaña, al volver acá
  // no queremos mostrarle un estado viejo.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const save = async (scope, id, body, okMsg) => {
    setBusy(`${scope}:${id}`);
    try {
      const url = scope === "group" ? `/email-switches/groups/${id}` : `/email-switches/${id}`;
      const r = await api.put(url, body);
      setData(r.data);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar");
    } finally {
      setBusy("");
    }
  };

  const confirmPause = async () => {
    if (!pausing || !pauseDate) return;
    await save(pausing.scope, pausing.id, { paused_until: pauseDate },
      `Apagado hasta el ${pauseDate}. Se reactiva solo.`);
    setPausing(null);
    setPauseDate("");
  };

  if (loading || !data) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
      </div>
    );
  }

  return (
    <section className="space-y-5" data-testid="mail-center">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-1">
        <p className="flex items-center gap-2 font-semibold">
          <MailWarning className="h-4 w-4" /> Cómo funciona
        </p>
        <p>
          Un correo sale solo si su grupo está encendido y él también. Los envíos
          <strong> a mano siempre salen</strong> (el ✈ de la factura, un boletín enviado por vos).
        </p>
        <p className="flex items-center gap-1">
          <Lock className="h-3.5 w-3.5" />
          Los correos de acceso (enlace de ingreso, invitación de cuenta) no se pueden apagar
          y por eso no están en esta lista.
        </p>
        {data.is_preview && (
          <p className="text-amber-800">
            Estás en el ambiente de pruebas: acá los automáticos no se disparan, así que
            "última vez que salió" se va a ver vacío. No es un error.
          </p>
        )}
      </div>

      {data.groups.map((g) => (
        <div
          key={g.group}
          className="bg-white border border-slate-200 rounded-xl overflow-hidden"
          data-testid={`mail-group-${g.group}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3 p-5 border-b border-slate-200 bg-slate-50">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-slate-900">{g.label}</h3>
                {g.effective_enabled ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                    Activos
                  </Badge>
                ) : (
                  <Badge className="bg-rose-100 text-rose-800 border border-rose-200">
                    {g.paused_until ? "En pausa" : "Apagados"}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">{g.note}</p>
              {g.paused_until && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Se reactiva el {fmtDate(g.paused_until)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setPausing({ scope: "group", id: g.group, label: g.label }); setPauseDate(""); }}
                data-testid={`pause-group-${g.group}`}
              >
                <PauseCircle className="h-4 w-4 mr-1" /> Pausar hasta…
              </Button>
              <Switch
                checked={g.enabled && !g.paused_until}
                disabled={busy === `group:${g.group}`}
                onCheckedChange={(v) => save("group", g.group, { enabled: !!v },
                  v ? `Grupo ${g.label}: correos activos` : `Grupo ${g.label}: correos apagados`)}
                data-testid={`toggle-group-${g.group}`}
              />
            </div>
          </div>

          <ul className="divide-y divide-slate-100">
            {g.kinds.map((k) => (
              <li
                key={k.kind}
                className="flex flex-wrap items-start justify-between gap-3 px-5 py-4"
                data-testid={`mail-kind-${k.kind}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900">{k.label}</span>
                    {k.always_on && (
                      <Badge variant="secondary" className="text-[11px]" data-testid={`mail-always-on-${k.kind}`}>
                        <Lock className="h-3 w-3 mr-1" /> No se puede apagar
                      </Badge>
                    )}
                    {!k.always_on && !k.effective_enabled && (
                      <Badge className="bg-rose-100 text-rose-800 border border-rose-200 text-[11px]">
                        {k.paused_until ? "En pausa" : "Apagado"}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{k.note}</p>
                  <p className="text-xs text-slate-600 mt-1" data-testid={`mail-last-${k.kind}`}>
                    {k.last_run_at
                      ? <>Última vez: {fmtDate(k.last_run_at)} · a {k.last_sent_count ?? 0} persona{(k.last_sent_count === 1) ? "" : "s"}</>
                      : <span className="text-slate-400">Todavía no hay registro de envíos</span>}
                  </p>
                  {k.last_skipped_at && (
                    <p className="text-xs text-amber-700 mt-0.5">
                      Omitido por estar apagado el {fmtDate(k.last_skipped_at)}
                    </p>
                  )}
                  {k.paused_until && (
                    <p className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Se reactiva el {fmtDate(k.paused_until)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {!k.always_on && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setPausing({ scope: "kind", id: k.kind, label: k.label }); setPauseDate(""); }}
                      data-testid={`pause-kind-${k.kind}`}
                    >
                      <PauseCircle className="h-4 w-4" />
                    </Button>
                  )}
                  <Switch
                    checked={k.always_on || (k.enabled && !k.paused_until)}
                    disabled={k.always_on || busy === `kind:${k.kind}`}
                    onCheckedChange={(v) => save("kind", k.kind, { enabled: !!v },
                      v ? `${k.label}: activo` : `${k.label}: apagado`)}
                    data-testid={`toggle-kind-${k.kind}`}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <Dialog open={!!pausing} onOpenChange={(o) => !o && setPausing(null)}>
        <DialogContent data-testid="pause-until-dialog">
          <DialogHeader>
            <DialogTitle>Pausar hasta una fecha</DialogTitle>
            <DialogDescription>
              {pausing?.label}: no va a salir nada hasta esa fecha y se reactiva solo
              a la medianoche de Costa Rica.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Se reactiva el</Label>
            <Input
              type="date"
              value={pauseDate}
              onChange={(e) => setPauseDate(e.target.value)}
              data-testid="pause-until-date"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPausing(null)}>Cancelar</Button>
            <Button
              className="btn-brand"
              disabled={!pauseDate}
              onClick={confirmPause}
              data-testid="pause-until-confirm"
            >
              Pausar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
